import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { onAuthStateChanged } from 'firebase/auth'
import { auth, db } from '../firebase'
import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc
} from 'firebase/firestore'
import SectionAd from '../components/ads/SectionAd'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import CreditIcon from '../components/CreditIcon'
import InjuryIcon from '../components/InjuryIcon'
import { getBrandBadgeClass } from '../utils/brandBadge'
import { usePremium } from '../premium/PremiumContext'

const getPremiumPositionLabel = value => {
  const position = Number(value) || 0
  if (position === 5) return 'Main Eventer'
  if (position === 4) return 'Upper'
  if (position === 3) return 'Mid'
  if (position === 2) return 'Low'
  if (position === 1) return 'Jobber'
  return 'Non TV'
}

const getPremiumPositionClassName = value => {
  const position = Number(value) || 0

  if (position >= 5) return 'text-wrestlers-division-5'
  if (position === 4) return 'text-wrestlers-division-4'
  if (position === 3) return 'text-wrestlers-division-3'
  if (position === 2) return 'text-wrestlers-division-2'
  if (position === 1) return 'text-wrestlers-division-1'
  return 'text-wrestlers-division-0'
}

const PREMIUM_POSITION_OPTIONS = [
  {
    value: 5,
    label: 'Main Eventer',
    toneClassName: 'text-wrestlers-division-5'
  },
  {
    value: 4,
    label: 'Upper',
    toneClassName: 'text-wrestlers-division-4'
  },
  {
    value: 3,
    label: 'Mid',
    toneClassName: 'text-wrestlers-division-3'
  },
  {
    value: 2,
    label: 'Low',
    toneClassName: 'text-wrestlers-division-2'
  },
  {
    value: 1,
    label: 'Jobber',
    toneClassName: 'text-wrestlers-division-1'
  },
  {
    value: 0,
    label: 'Non TV',
    toneClassName: 'text-wrestlers-division-0'
  }
]

const ALL_PREMIUM_POSITION_VALUES = PREMIUM_POSITION_OPTIONS.map(
  option => option.value
)

function Wrestlers() {
  const [wrestlers, setWrestlers] = useState([])
  const [currentUser, setCurrentUser] = useState(null)
  const [favoriteWrestlerIds, setFavoriteWrestlerIds] = useState([])
  const [favoriteFilter, setFavoriteFilter] = useState(false)
  const [favoriteEditMode, setFavoriteEditMode] = useState(false)
  const [favoriteSavingId, setFavoriteSavingId] = useState('')
  const [viewMode, setViewMode] = useState('grid')
  const [sortOrder, setSortOrder] = useState('asc')
  const [sortField, setSortField] = useState('name')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [brandFilter, setBrandFilter] = useState('All')
  const [genderFilter, setGenderFilter] = useState('All')
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [selectedPositions, setSelectedPositions] = useState(() => [
    ...ALL_PREMIUM_POSITION_VALUES
  ])
  const [positionFilterOpen, setPositionFilterOpen] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const navigate = useNavigate()
  const [championFilter, setChampionFilter] = useState(false)
  const [teamFilter, setTeamFilter] = useState(false)
  const [injuredFilter, setInjuredFilter] = useState(false)
  const [teams, setTeams] = useState([])
  const positionFilterRef = useRef(null)
  const [scoreView, setScoreView] = useState('current')
  const { isPremium, premiumLoading } = usePremium()

  // Hook useMemo per dati derivati
  const uniqueBrands = useMemo(() => {
    const brands = new Set(wrestlers.map(w => w.brand).filter(Boolean))
    return ['All', ...Array.from(brands).sort()]
  }, [wrestlers])

  const uniqueGenders = useMemo(() => {
    const genders = new Set(wrestlers.map(w => w.gender).filter(Boolean))
    return ['All', ...Array.from(genders).sort()]
  }, [wrestlers])

  const availableScoreViews = useMemo(() => {
    const maxSeasonCount = wrestlers.reduce(
      (max, wrestler) =>
        Math.max(
          max,
          Array.isArray(wrestler.scores) ? wrestler.scores.length : 0
        ),
      0
    )
    return [
      { value: 'current', label: 'Punteggio attuale' },
      ...Array.from({ length: maxSeasonCount }).map((_, index) => ({
        value: `season-${index}`,
        label: `Season ${index}`
      }))
    ]
  }, [wrestlers])

  const getDisplayedScore = useCallback(
    wrestler => {
      if (scoreView === 'current') return wrestler.score ?? 0
      const seasonIndex = Number(scoreView.replace('season-', ''))
      return Array.isArray(wrestler.scores)
        ? wrestler.scores[seasonIndex] ?? 0
        : 0
    },
    [scoreView]
  )

  const isFavorite = useCallback(
    wrestlerId => favoriteWrestlerIds.includes(wrestlerId),
    [favoriteWrestlerIds]
  )

  const hasCustomPositionFilter = useMemo(
    () => selectedPositions.length !== ALL_PREMIUM_POSITION_VALUES.length,
    [selectedPositions]
  )

  const selectedPositionSummary = useMemo(() => {
    if (selectedPositions.length === ALL_PREMIUM_POSITION_VALUES.length) {
      return 'Tutte le posizioni'
    }

    if (selectedPositions.length === 0) {
      return 'Nessuna posizione'
    }

    const labels = PREMIUM_POSITION_OPTIONS.filter(option =>
      selectedPositions.includes(option.value)
    ).map(option => option.label)

    if (labels.length <= 2) {
      return labels.join(', ')
    }

    return `${labels.length} posizioni selezionate`
  }, [selectedPositions])

  const filteredWrestlers = useMemo(() => {
    // Filtraggio
    let result = wrestlers.filter(w => {
      const matchesSearch =
        searchQuery.trim() === '' ||
        w.name.toLowerCase().includes(searchQuery.toLowerCase())
      const isActive = w.active === true && !w.isGuest

      // Filtro brand - si escludono a vicenda
      const matchesBrand = brandFilter === 'All' || w.brand === brandFilter

      const matchesGender = genderFilter === 'All' || w.gender === genderFilter

      // Filtro infortunati - può essere combinato con altri
      const matchesInjured = !injuredFilter || w.isInjured

      // Filtro team - può essere combinato con altri
      const matchesTeam =
        !teamFilter || teams.some(team => team.members?.includes(w.id))

      // Filtro per prezzo
      const price = w.price || 0
      const matchesMinPrice = minPrice === '' || price >= Number(minPrice)
      const matchesMaxPrice = maxPrice === '' || price <= Number(maxPrice)

      // Filtro campioni - può essere combinato con altri
      const matchesChampion =
        !championFilter ||
        (championFilter &&
          w.championTypes &&
          w.championTypes.length > 0 &&
          w.championTypes.some(type => type !== ''))
      const matchesFavorite =
        !favoriteFilter || favoriteWrestlerIds.includes(w.id)
      const position = Number(w.position) || 0
      const matchesSelectedPositions =
        !isPremium || selectedPositions.includes(position)

      return (
        isActive &&
        matchesSearch &&
        matchesBrand &&
        matchesGender &&
        matchesMinPrice &&
        matchesMaxPrice &&
        matchesChampion &&
        matchesTeam &&
        matchesInjured &&
        matchesFavorite &&
        matchesSelectedPositions
      )
    })

    // Raggruppa per team solo se il filtro team è attivo
    if (teamFilter) {
      const wrestlersByTeam = {}
      teams.forEach(team => {
        const teamWrestlers = result.filter(w => team.members?.includes(w.id))
        if (teamWrestlers.length > 0) {
          wrestlersByTeam[team.name] = teamWrestlers
        }
      })

      // Crea un array ordinato con i wrestler raggruppati per team
      const sortedResult = []
      Object.keys(wrestlersByTeam)
        .sort()
        .forEach(teamName => {
          sortedResult.push({ isTeamHeader: true, teamName })
          sortedResult.push(...wrestlersByTeam[teamName])
        })

      // Aggiungi i wrestler senza team alla fine
      const noTeamWrestlers = result.filter(
        w => !teams.some(team => team.members?.includes(w.id))
      )
      if (noTeamWrestlers.length > 0) {
        sortedResult.push({ isTeamHeader: true, teamName: 'Senza Team' })
        sortedResult.push(...noTeamWrestlers)
      }

      return sortedResult
    }

    // Ordinamento normale se non è attivo il filtro team
    return result.sort((a, b) => {
      let aValue, bValue

      if (sortField === 'price') {
        aValue = a.price || 0
        bValue = b.price || 0
      } else if (sortField === 'score') {
        aValue = getDisplayedScore(a)
        bValue = getDisplayedScore(b)
      } else if (sortField === 'position') {
        aValue = Number(a[sortField]) || 0
        bValue = Number(b[sortField]) || 0
      } else {
        aValue = a[sortField] || (typeof a[sortField] === 'number' ? 0 : '')
        bValue = b[sortField] || (typeof b[sortField] === 'number' ? 0 : '')
      }

      // Ordinamento principale
      let comparison
      if (sortOrder === 'asc') {
        comparison =
          typeof aValue === 'number'
            ? aValue - bValue
            : aValue.localeCompare(bValue)
      } else {
        comparison =
          typeof aValue === 'number'
            ? bValue - aValue
            : bValue.localeCompare(aValue)
      }

      // Se i valori sono uguali, ordina per nome
      if (comparison === 0) {
        return a.name.localeCompare(b.name)
      }

      return comparison
    })
  }, [
    wrestlers,
    searchQuery,
    brandFilter,
    genderFilter,
    sortField,
    sortOrder,
    minPrice,
    maxPrice,
    selectedPositions,
    championFilter,
    teamFilter,
    injuredFilter,
    favoriteFilter,
    favoriteWrestlerIds,
    isPremium,
    teams,
    getDisplayedScore
  ])

  // Effetti
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      setCurrentUser(user)
      setFavoriteEditMode(false)

      if (!user) {
        setFavoriteWrestlerIds([])
        setFavoriteFilter(false)
        return
      }

      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid))
        setFavoriteWrestlerIds(
          Array.isArray(userSnap.data()?.favoriteWrestler)
            ? userSnap.data().favoriteWrestler
            : []
        )
      } catch (favoriteError) {
        console.error('Errore caricamento preferiti:', favoriteError)
        setFavoriteWrestlerIds([])
      }
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (isPremium) return

    setSelectedPositions([...ALL_PREMIUM_POSITION_VALUES])
    setPositionFilterOpen(false)
    setSortField(prev => (prev === 'position' ? 'name' : prev))
  }, [isPremium])

  useEffect(() => {
    if (!positionFilterOpen) return undefined

    const handleClickOutside = event => {
      if (
        positionFilterRef.current &&
        !positionFilterRef.current.contains(event.target)
      ) {
        setPositionFilterOpen(false)
      }
    }

    const handleEscape = event => {
      if (event.key === 'Escape') {
        setPositionFilterOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [positionFilterOpen])

  const fetchTeams = useCallback(async () => {
    try {
      const teamsSnapshot = await getDocs(collection(db, 'teams'))
      const teamsData = teamsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setTeams(teamsData)
    } catch (err) {
      console.error('Error fetching teams:', err)
    }
  }, [])

  const fetchWrestlers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const snap = await getDocs(collection(db, 'wrestlers'))
      const data = snap.docs.map(doc => {
        const docData = doc.data()
        const { position, ...standardDocData } = docData

        // Ottieni l'ultimo prezzo dall'array prices
        const prices = standardDocData.prices || []
        const lastPrice =
          prices.length > 0 ? prices[prices.length - 1].value : 0

        return {
          id: doc.id,
          name: standardDocData.name || standardDocData.nome || 'Sconosciuto',
          score: standardDocData.score ?? 0,
          scores: Array.isArray(standardDocData.scores)
            ? standardDocData.scores
            : Array.isArray(standardDocData.pastScores)
              ? standardDocData.pastScores
              : [],
          imageUrl: standardDocData.imageUrl || '',
          price: lastPrice, // Usa l'ultimo prezzo da prices
          brand: standardDocData.brand,
          gender: standardDocData.gender,
          active: standardDocData.active ?? true,
          // Carica il flag infortunato guardando diversi possibili nomi di campo
          isInjured:
            standardDocData.isInjured ??
            standardDocData.is_injured ??
            standardDocData.injured ??
            false,
          // Mantieni compatibilità con vecchi dati
          championTypes: standardDocData.championTypes,
          championImages: standardDocData.championImages,
          ...(isPremium
            ? {
              position: position ?? 0
            }
            : {}),
          // Salva anche l'intero array prices se ti serve
          prices: prices
        }
      })

      setWrestlers(data)
    } catch (err) {
      console.error('Errore nel caricamento:', err)
      setError(err.message || 'Errore nel caricamento dei dati')
    } finally {
      setLoading(false)
    }
  }, [isPremium])

  useEffect(() => {
    if (premiumLoading) return

    fetchWrestlers()
    fetchTeams()
  }, [fetchTeams, fetchWrestlers, premiumLoading])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        setError('Il caricamento sta impiegando più tempo del previsto')
        setLoading(false)
      }
    }, 10000)

    return () => clearTimeout(timeout)
  }, [loading])

  // Handlers
  const handleSort = field => {
    setSortOrder(prev =>
      sortField === field && prev === 'asc' ? 'desc' : 'asc'
    )
    setSortField(field)
  }

  const sortIcon = field => {
    if (sortField !== field) return null
    return sortOrder === 'asc' ? (
      <i className='bi bi-caret-up-fill ms-1' />
    ) : (
      <i className='bi bi-caret-down-fill ms-1' />
    )
  }

  const clearFilters = () => {
    setBrandFilter('All')
    setGenderFilter('All')
    setSearchQuery('')
    setMinPrice('')
    setMaxPrice('')
    setSelectedPositions([...ALL_PREMIUM_POSITION_VALUES])
    setPositionFilterOpen(false)
    setChampionFilter(false)
    setTeamFilter(false)
    setInjuredFilter(false)
    setFavoriteFilter(false)
    setSortField('name')
    setSortOrder('asc')
  }

  const handleToggleFavorite = async (event, wrestlerId) => {
    event.preventDefault()
    event.stopPropagation()

    if (!currentUser || favoriteSavingId) return

    const userRef = doc(db, 'users', currentUser.uid)
    const alreadyFavorite = favoriteWrestlerIds.includes(wrestlerId)

    setFavoriteSavingId(wrestlerId)
    setFavoriteWrestlerIds(prev =>
      alreadyFavorite
        ? prev.filter(id => id !== wrestlerId)
        : [...prev, wrestlerId]
    )

    try {
      await updateDoc(userRef, {
        favoriteWrestler: alreadyFavorite
          ? arrayRemove(wrestlerId)
          : arrayUnion(wrestlerId)
      })
    } catch (favoriteError) {
      console.error('Errore salvataggio preferiti:', favoriteError)
      setFavoriteWrestlerIds(prev =>
        alreadyFavorite
          ? [...prev, wrestlerId]
          : prev.filter(id => id !== wrestlerId)
      )
    } finally {
      setFavoriteSavingId('')
    }
  }

  // Handler per filtri brand che si escludono a vicenda
  const handleBrandFilter = brand => {
    setBrandFilter(brandFilter === brand ? 'All' : brand)
  }

  const togglePositionValue = value => {
    setSelectedPositions(prev => {
      const nextSelected = prev.includes(value)
        ? prev.filter(position => position !== value)
        : [...prev, value]

      return PREMIUM_POSITION_OPTIONS.map(option => option.value).filter(
        optionValue => nextSelected.includes(optionValue)
      )
    })
  }

  const selectAllPositions = () => {
    setSelectedPositions([...ALL_PREMIUM_POSITION_VALUES])
  }

  const clearPositionSelection = () => {
    setSelectedPositions([])
  }

  const getBrandImageBackground = brand => {
    switch (brand) {
      case 'Raw':
        return 'linear-gradient(180deg, #5a0b12 0%, #b91c1c00 100%)'
      case 'SmackDown':
        return 'linear-gradient(180deg, #0b2a5a 0%, #2563eb00 100%)'
      case 'NXT':
        return 'linear-gradient(180deg, #4b4b4b 0%, #4b4b4b00 100%)'
      default:
        return null
    }
  }

  // Controlla se ci sono filtri attivi
  const hasActiveFilters =
    brandFilter !== 'All' ||
    genderFilter !== 'All' ||
    searchQuery ||
    minPrice ||
    maxPrice ||
    (isPremium && hasCustomPositionFilter) ||
    championFilter ||
    teamFilter ||
    injuredFilter ||
    favoriteFilter ||
    sortField !== 'name' ||
    sortOrder !== 'asc'

  // Render condizionali
  if (loading) return <LoadingSpinner fullPage={true} />

  if (error) {
    return (
      <div className='wrestlers-page container py-5 text-center'>
        <div className='factions-error-box'>
          <i className='bi bi-exclamation-triangle-fill me-2'></i>
          {error}
        </div>
        <button
          className='stats-action-btn mt-3'
          onClick={() => window.location.reload()}
        >
          <i className='bi bi-arrow-clockwise me-2'></i>
          Ricarica
        </button>
      </div>
    )
  }

  return (
    <div className='wrestlers-page wrestlers-container container-fluid p-0'>
      {/* Sezione filtri che scorre con il contenuto */}
      <div className='wrestlers-topbar py-3'>
        <div className='container'>
          <h1
            className='mb-3 fw-bold text-center text-brand-secondary'
            style={{
              fontFamily: "'Cinzel Decorative'",
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.45)'
            }}
          >
            <i className='bi bi-people-fill me-1' />
            WreStleR
          </h1>

          {/* FILTRI RAPIDI */}
          <div className='mt-3'>
            {/* Barra di ricerca sempre visibile */}
            <div className='mb-3'>
              <div className='input-group input-group-sm wrestlers-search-input-group'>
                <span className='input-group-text wrestlers-search-addon'>
                  <i className='bi bi-search text-brand-primary-ultralight' />
                </span>
                <input
                  type='text'
                  className='form-control wrestlers-search-input'
                  placeholder='Cerca wrestler...'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Pulsanti rapidi con scroll orizzontale */}
            <div className='mb-3'>
              <div className='quick-filter-buttons'>
                {/* Ordinamenti rapidi */}
                <button
                  className={`btn-filter-sort ${sortField === 'score' && sortOrder === 'desc'
                      ? 'active'
                      : ''
                    }`}
                  onClick={() => {
                    setSortField('score')
                    setSortOrder('desc')
                  }}
                  title={`Classifica per ${scoreView === 'current'
                      ? 'punteggio attuale'
                      : scoreView.replace('season-', 'Season ')
                    }`}
                >
                  <i className='bi bi-trophy me-1'></i>
                  Classifica
                </button>

                <button
                  className={`btn-quotation ${sortField === 'price' && sortOrder === 'desc'
                      ? 'active'
                      : ''
                    }`}
                  onClick={() => {
                    setSortField('price')
                    setSortOrder('desc')
                  }}
                  title='Ordina per quotazione più alta'
                >
                  <CreditIcon className='me-1' size={16} />
                  Quotazione <i className='bi bi-arrow-up'></i>
                </button>

                <button
                  className={`btn-quotation-down ${sortField === 'price' && sortOrder === 'asc' ? 'active' : ''
                    }`}
                  onClick={() => {
                    setSortField('price')
                    setSortOrder('asc')
                  }}
                  title='Ordina per quotazione più bassa'
                >
                  <CreditIcon className='me-1' size={16} />
                  Quotazione <i className='bi bi-arrow-down'></i>
                </button>

                {isPremium && (
                  <>
                    <button
                      className={`btn-filter-combine ${sortField === 'position' && sortOrder === 'desc'
                          ? 'active'
                          : ''
                        }`}
                      onClick={() => {
                        setSortField('position')
                        setSortOrder('desc')
                      }}
                      title='Ordina per position piu alta'
                    >
                      <i className='bi bi-stars me-1'></i>
                      Posizione <i className='bi bi-arrow-up'></i>
                    </button>
                  </>
                )}

                {/* Separatore */}
                <div className='filter-divider'></div>

                {/* Filtri per brand - si escludono a vicenda */}
                <button
                  className={`btn-filter-brand raw ${brandFilter === 'Raw' ? 'active' : ''
                    }`}
                  onClick={() => handleBrandFilter('Raw')}
                  title='Filtra per Raw'
                >
                  Raw
                </button>

                <button
                  className={`btn-filter-brand smackdown ${brandFilter === 'SmackDown' ? 'active' : ''
                    }`}
                  onClick={() => handleBrandFilter('SmackDown')}
                  title='Filtra per SmackDown'
                >
                  SmackDown
                </button>

                <button
                  className={`btn-filter-brand nxt ${brandFilter === 'NXT' ? 'active' : 'text-light'
                    }`}
                  onClick={() => handleBrandFilter('NXT')}
                  title='Filtra per NXT'
                >
                  NXT
                </button>

                <button
                  className={`btn-filter-brand freeagent ${brandFilter === 'Free Agent' ? 'active' : ''
                    }`}
                  onClick={() => handleBrandFilter('Free Agent')}
                  title='Filtra per Free Agent'
                >
                  Free Agent
                </button>

                {/* Separatore */}
                <div className='filter-divider'></div>

                {/* Filtri che possono essere combinati */}
                <button
                  className={`btn-filter-combine ${championFilter ? 'active' : ''
                    }`}
                  onClick={() => setChampionFilter(!championFilter)}
                  title='Filtra per campioni'
                >
                  <i className='bi bi-award me-1'></i>
                  Campioni
                </button>

                <button
                  className={`btn-filter-combine ${teamFilter ? 'active' : ''}`}
                  onClick={() => setTeamFilter(!teamFilter)}
                  title='Raggruppa per team'
                >
                  <i className='bi bi-people-fill me-1'></i>
                  Teams
                </button>

                <button
                  className={`btn-injured ${injuredFilter ? 'active' : ''}`}
                  onClick={() => setInjuredFilter(!injuredFilter)}
                  title='Mostra solo infortunati'
                >
                  <i className='bi bi-plus-circle me-1'></i>
                  Infortunati
                </button>

                {currentUser && (
                  <button
                    className={`btn-filter-combine ${favoriteFilter ? 'active' : ''
                      }`}
                    onClick={() => setFavoriteFilter(!favoriteFilter)}
                    title='Mostra solo wrestler preferiti'
                    type='button'
                  >
                    <i
                      className={`bi ${favoriteFilter ? 'bi-star-fill' : 'bi-star'
                        } me-1`}
                    ></i>
                    Preferiti
                  </button>
                )}

                {/* Separatore */}
                <div className='filter-divider'></div>

                {/* Ordinamento manuale */}
                <select
                  className='filter-select'
                  value={scoreView}
                  onChange={e => setScoreView(e.target.value)}
                  title='Seleziona stagione punteggi'
                >
                  {availableScoreViews.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <select
                  className='filter-select'
                  value={sortField}
                  onChange={e => setSortField(e.target.value)}
                  title='Seleziona campo di ordinamento'
                >
                  <option value='name'>Nome</option>
                  <option value='score'>Punteggio</option>
                  <option value='price'>Quotazione</option>
                  <option value='gender'>Sesso</option>
                  <option value='brand'>Brand</option>
                  {isPremium && <option value='position'>Position</option>}
                </select>

                {/* Pulsante reset */}
                <button
                  className='btn-reset-filters'
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  title='Resetta filtri'
                >
                  <i className='bi bi-x-lg'></i>
                </button>
              </div>
            </div>

            <div className='wrestlers-toolbar-row'>
              <div className='wrestlers-toolbar-primary'>
                {/* Pulsante per mostrare/nascondere filtri su schermi piccoli */}
                <button
                  className='btn-filter-combine d-md-none'
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <i
                    className={`bi ${
                      showFilters ? 'bi-filter-circle-fill' : 'bi-filter'
                    } me-1`}
                  ></i>
                  Filtri
                </button>
              </div>

              <div className='wrestlers-toolbar-actions'>
                {/* Controlli visualizzazione */}
                <div
                  className='btn-group btn-group-sm flex-shrink-0'
                  style={{ gap: '0.25rem' }}
                >
                  <button
                    className={`btn-control ${
                      viewMode === 'grid'
                        ? 'active'
                        : 'text-brand-primary-ultralight'
                    }`}
                    onClick={() => setViewMode('grid')}
                    title='Vista griglia'
                  >
                    <i className='bi bi-grid-3x3-gap'></i>
                  </button>
                  <button
                    className={`btn-control ${
                      viewMode === 'list'
                        ? 'active'
                        : 'text-brand-primary-ultralight'
                    }`}
                    onClick={() => setViewMode('list')}
                    title='Vista lista'
                  >
                    <i className='bi bi-list-task'></i>
                  </button>
                </div>

                {currentUser && (
                  <div className='d-flex justify-content-center'>
                    <button
                      className={`btn-filter-combine ${
                        favoriteEditMode ? 'active' : ''
                      }`}
                      onClick={() => setFavoriteEditMode(prev => !prev)}
                      type='button'
                    >
                      <i
                        className={`bi ${
                          favoriteEditMode ? 'bi-star-fill' : 'bi-star'
                        } me-1`}
                      ></i>
                      <span className='d-none d-sm-inline'>
                        {favoriteEditMode ? 'Fine modifica' : 'Scegli preferiti'}
                      </span>
                      <span className='d-inline d-sm-none'>
                        {favoriteEditMode ? 'Fine' : 'Scegli'}
                      </span>
                    </button>
                  </div>
                )}

                <div
                  className='btn-group btn-group-sm flex-shrink-0'
                  style={{ gap: '0.25rem' }}
                >
                  <button
                    className={`btn-control ${
                      sortOrder === 'asc'
                        ? 'active'
                        : 'text-brand-primary-ultralight'
                    }`}
                    onClick={() => setSortOrder('asc')}
                    title='Crescente'
                  >
                    <i className='bi bi-sort-down' />
                  </button>
                  <button
                    className={`btn-control ${
                      sortOrder === 'desc'
                        ? 'active'
                        : 'text-brand-primary-ultralight'
                    }`}
                    onClick={() => setSortOrder('desc')}
                    title='Decrescente'
                  >
                    <i className='bi bi-sort-up' />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* FILTRI AVANZATI - Espandibili */}
          <div
            className={`${showFilters ? 'd-block' : 'd-none d-md-block'} mt-3`}
          >
            <div className='card border-0 shadow-sm wrestlers-advanced-card'>
              <div className='card-header py-2 wrestlers-advanced-header'>
                <h6 className='mb-0 d-flex align-items-center text-brand-secondary'>
                  <i className='bi bi-funnel me-2'></i>
                  Filtri Avanzati
                </h6>
              </div>
              <div className='card-body py-3 wrestlers-advanced-body'>
                <div className='wrestlers-advanced-grid'>
                  {/* Filtro per brand */}
                  <div className='wrestlers-filter-field'>
                    <label className='form-label small fw-bold text-muted-brand'>
                      Brand
                    </label>
                    <select
                      className='form-select form-select-sm stats-control'
                      value={brandFilter}
                      onChange={e => setBrandFilter(e.target.value)}
                    >
                      {uniqueBrands.map(brand => (
                        <option key={brand} value={brand}>
                          {brand}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro per genere */}
                  <div className='wrestlers-filter-field'>
                    <label className='form-label small fw-bold text-muted-brand'>
                      Sesso
                    </label>
                    <select
                      className='form-select form-select-sm stats-control'
                      value={genderFilter}
                      onChange={e => setGenderFilter(e.target.value)}
                    >
                      {uniqueGenders.map(gender => (
                        <option key={gender} value={gender}>
                          {gender}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro per prezzo */}
                  <div className='wrestlers-filter-field'>
                    <label className='form-label small fw-bold text-muted-brand'>
                      Quotazione
                    </label>
                    <div className='input-group input-group-sm'>
                      <input
                        type='number'
                        className='form-control stats-control '
                        placeholder='Min'
                        value={minPrice}
                        onChange={e => setMinPrice(e.target.value)}
                        min='0'
                      />
                      <span className='input-group-text wrestlers-price-separator'>
                        -
                      </span>
                      <input
                        type='number'
                        className='form-control stats-control'
                        placeholder='Max'
                        value={maxPrice}
                        onChange={e => setMaxPrice(e.target.value)}
                        min='0'
                      />
                    </div>
                  </div>

                  {isPremium && (
                    <div
                      className='wrestlers-filter-field wrestlers-filter-field--position'
                      ref={positionFilterRef}
                    >
                      <label className='form-label small fw-bold text-muted-brand d-flex align-items-center gap-1'>
                        <i className='bi bi-stars text-brand-secondary'></i>
                        Posizione
                      </label>
                      <button
                        type='button'
                        className={`wrestlers-position-dropdown-toggle ${
                          positionFilterOpen ? 'is-open' : ''
                        }`.trim()}
                        onClick={() =>
                          setPositionFilterOpen(prevOpen => !prevOpen)
                        }
                        aria-expanded={positionFilterOpen}
                      >
                        <span className='wrestlers-position-dropdown-label'>
                          {selectedPositionSummary}
                        </span>
                        <span className='wrestlers-position-dropdown-count'>
                          {selectedPositions.length}/
                          {ALL_PREMIUM_POSITION_VALUES.length}
                        </span>
                        <i
                          className={`bi ${
                            positionFilterOpen
                              ? 'bi-chevron-up'
                              : 'bi-chevron-down'
                          }`}
                          aria-hidden='true'
                        ></i>
                      </button>

                      {positionFilterOpen && (
                        <div className='wrestlers-position-dropdown-menu'>
                          <div className='wrestlers-position-dropdown-actions'>
                            <button
                              type='button'
                              className='wrestlers-position-dropdown-action'
                              onClick={selectAllPositions}
                            >
                              Tutte
                            </button>
                            <button
                              type='button'
                              className='wrestlers-position-dropdown-action'
                              onClick={clearPositionSelection}
                            >
                              Nessuna
                            </button>
                          </div>

                          <div className='wrestlers-position-dropdown-options'>
                            {PREMIUM_POSITION_OPTIONS.map(option => {
                              const isSelected = selectedPositions.includes(
                                option.value
                              )

                              return (
                                <label
                                  key={option.value}
                                  className={`wrestlers-position-option ${
                                    isSelected ? 'is-selected' : ''
                                  }`.trim()}
                                >
                                  <input
                                    type='checkbox'
                                    className='form-check-input wrestlers-position-option__checkbox'
                                    checked={isSelected}
                                    onChange={() =>
                                      togglePositionValue(option.value)
                                    }
                                  />
                                  <span className='wrestlers-position-option__content'>
                                    <span
                                      className={`wrestlers-position-option__pill ${option.toneClassName}`.trim()}
                                    >
                                      {option.label}
                                    </span>
                                  </span>
                                </label>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenuto scorrevole */}
      <div className='container mt-3'>
        {filteredWrestlers.length === 0 ? (
          <div className='wrestlers-empty-state text-center py-5'>
            <i className='bi bi-emoji-frown fs-1 text-muted-brand'></i>
            <h4 className='mt-3 text-muted-brand'>Nessun wrestler trovato</h4>
            <p>
              I wrestler che stai cercando non sono presenti o non sono più
              attivi.
            </p>
            {hasActiveFilters && (
              <button className='btn-reset-filters mt-3' onClick={clearFilters}>
                Cancella i filtri
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className='row g-2'>
            {filteredWrestlers.map(w =>
              w.isTeamHeader ? (
                <div key={w.teamName} className='col-12'>
                  <h5 className='mt-4 mb-2 border-bottom pb-2 wrestlers-team-header'>
                    {w.teamName}
                  </h5>
                </div>
              ) : (
                <div key={w.id} className='col-6 col-sm-4 col-md-3 col-lg-2'>
                  <Link
                    to={`/wrestlers/${w.id}`}
                    className='text-decoration-none'
                  >
                    <div className='wrestler-card wrestlers-grid-card shadow-sm position-relative overflow-hidden'>
                      <div className='position-relative'>
                        {/* Injury badge */}
                        {w.isInjured && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 10,
                              left: 10,
                              zIndex: 10
                            }}
                          >
                            <InjuryIcon />
                          </div>
                        )}

                        {/* Badge del campione in alto a sinistra */}
                        {w.championImages && w.championImages.length > 0 && (
                          <div
                            className=''
                            style={{
                              position: 'absolute',
                              top: 10,
                              left: 15,
                              zIndex: 3,
                              width: '40px',
                              height: '40px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              gap: '2px'
                            }}
                          >
                            {w.championImages
                              .map(
                                (img, index) =>
                                  img && (
                                    <div
                                      key={index}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        // Il secondo titolo viene spostato leggermente in basso
                                        marginTop: index === 1 ? '18px' : '0',
                                        // Sovrapposizione: il secondo titolo ha z-index inferiore
                                        zIndex: 3 - index,
                                        position:
                                          index === 1 ? 'absolute' : 'relative'
                                      }}
                                    >
                                      <img
                                        src={img}
                                        alt={
                                          w.championTypes
                                            ? w.championTypes[index]
                                            : 'Champion'
                                        }
                                        style={{
                                          height: '25px',
                                          objectFit: 'contain'
                                        }}
                                      />
                                    </div>
                                  )
                              )
                              .filter(Boolean)}
                          </div>
                        )}

                        {favoriteEditMode && currentUser ? (
                          <button
                            type='button'
                            className='btn btn-link position-absolute top-0 end-0 m-2 p-0 text-decoration-none'
                            onClick={event => handleToggleFavorite(event, w.id)}
                            aria-label={
                              isFavorite(w.id)
                                ? `Rimuovi ${w.name} dai preferiti`
                                : `Aggiungi ${w.name} ai preferiti`
                            }
                            disabled={favoriteSavingId === w.id}
                            style={{ zIndex: 11 }}
                          >
                            <i
                              className={`bi ${isFavorite(w.id)
                                  ? 'bi-star-fill text-warning'
                                  : 'bi-star text-light'
                                }`}
                              style={{ fontSize: '1.2rem' }}
                            ></i>
                          </button>
                        ) : (
                          <span className='position-absolute top-0 end-0 m-2 fw-bold text-light'>
                            {getDisplayedScore(w)}
                          </span>
                        )}
                        <div
                          style={{
                            height: '160px',
                            background: getBrandImageBackground(w.brand)
                          }}
                        >
                          <img
                            src={w.imageUrl || '/placeholder-wrestler.jpg'}
                            alt={w.name}
                            className='img-fluid w-100'
                            style={{
                              height: '160px',
                              objectFit: 'cover',
                              objectPosition: 'top center'
                            }}
                          />
                        </div>
                        <div className='position-absolute bottom-0 start-0 end-0 p-2 name-overlay'>
                          <div className='d-flex justify-content-between align-items-end mt-1'>
                            <h6 className='text-white text-truncate mb-0 fw-semibold'>
                              {w.name}
                            </h6>
                          </div>
                          {isPremium && (
                            <div className='wrestlers-premium-division'>
                              <span
                                className={getPremiumPositionClassName(
                                  w.position
                                )}
                              >
                                {getPremiumPositionLabel(w.position)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              )
            )}
          </div>
        ) : (
          // Vista tabella con scrolling orizzontale
          <div
            className='table-responsive rounded-3 shadow-sm wrestlers-table-wrapper'
            style={{ overflowX: 'auto' }}
          >
            <table className='table table-striped table-hover table-dark align-middle mb-0 wrestlers-table'>
              <thead>
                <tr className=''>
                  <th
                    scope='col'
                    className='ps-3 py-2 text-uppercase fw-bold text-light'
                    onClick={() => handleSort('name')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className='d-flex align-items-start'>
                      Wrestler
                      {sortIcon('name')}
                    </div>
                  </th>
                  <th
                    scope='col'
                    className='ps-3 py-2 text-uppercase fw-bold text-light'
                    onClick={() => handleSort('brand')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className='d-flex align-items-center justify-content-center'>
                      Brand
                      {sortIcon('brand')}
                    </div>
                  </th>
                  <th
                    scope='col'
                    className='ps-3 py-2 text-uppercase fw-bold text-light'
                    onClick={() => handleSort('price')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className='d-flex align-items-center justify-content-center'>
                      Quotazione
                      {sortIcon('price')}
                    </div>
                  </th>
                  {isPremium && (
                    <th
                      scope='col'
                      className='ps-3 py-2 text-uppercase fw-bold text-light'
                      onClick={() => handleSort('position')}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className='d-flex align-items-center justify-content-center gap-1'>
                        <i className='bi bi-stars text-brand-secondary'></i>
                        Divisione
                        {sortIcon('position')}
                      </div>
                    </th>
                  )}
                  <th
                    scope='col'
                    className='pe-3 py-2 text-uppercase fw-bold text-light text-end'
                    onClick={() => handleSort('score')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className='d-flex justify-content-end'>
                      {scoreView === 'current'
                        ? 'Punteggio'
                        : scoreView.replace('season-', 'Season ')}
                      {sortIcon('score')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredWrestlers.map(w => (
                  <tr
                    key={w.id}
                    role='button'
                    onClick={() => navigate(`/wrestlers/${w.id}`)}
                    className='cursor-pointer wrestlers-table-row'
                  >
                    <td className='ps-3 py-2'>
                      <div className='d-flex align-items-center'>
                        <span className='fw-semibold text-white me-2'>
                          {w.name}
                        </span>{' '}
                        {w.gender === 'Uomo' ? (
                          <i
                            className='bi bi-gender-male text-brand-primary-ultralight'
                            title='Uomo'
                          ></i>
                        ) : w.gender === 'Donna' ? (
                          <i
                            className='bi bi-gender-female text-brand-accent-light'
                            title='Donna'
                          ></i>
                        ) : (
                          <i
                            className='bi bi-gender-ambiguous text-muted-brand'
                            title={w.gender || 'N/A'}
                          ></i>
                        )}
                        {w.isInjured && (
                          <InjuryIcon
                            size={18}
                            iconSize='0.85rem'
                            className='ms-2'
                          />
                        )}
                      </div>
                    </td>
                    <td className='text-center' style={{ fontSize: '0.75rem' }}>
                      <span className={getBrandBadgeClass(w.brand)}>
                        {w.brand}
                      </span>
                    </td>
                    <td className='ps-3 py-2 text-center'>
                      <span className='text-money fw-bold'>{w.price}</span>
                    </td>
                    {isPremium && (
                      <td className='ps-3 py-2 text-center'>
                        <span className='wrestlers-premium-division wrestlers-premium-division--table'>
                          <span
                            className={getPremiumPositionClassName(w.position)}
                          >
                            {getPremiumPositionLabel(w.position)}
                          </span>
                        </span>
                      </td>
                    )}
                    <td className='pe-3 py-2 text-end'>
                      {favoriteEditMode && currentUser ? (
                        <button
                          type='button'
                          className='btn btn-link p-0 text-decoration-none'
                          onClick={event => handleToggleFavorite(event, w.id)}
                          aria-label={
                            isFavorite(w.id)
                              ? `Rimuovi ${w.name} dai preferiti`
                              : `Aggiungi ${w.name} ai preferiti`
                          }
                          disabled={favoriteSavingId === w.id}
                        >
                          <i
                            className={`bi ${isFavorite(w.id)
                                ? 'bi-star-fill text-warning'
                                : 'bi-star text-light'
                              }`}
                          ></i>
                        </button>
                      ) : (
                        <span className='fw-bold text-light'>
                          {getDisplayedScore(w)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <SectionAd
          placement='home'
          className='d-flex justify-content-center my-2'
        />
      </div>
    </div>
  )
}

export default Wrestlers
